/**
 * The single home of business identity.
 *
 * Nothing outside src/theme/ may contain a business name, a logo, a colour, or
 * a piece of brand copy. scripts/check-boundaries.ts enforces that.
 *
 * BrandConfig holds NO business logic. Currency *formatting* lives here;
 * currency *arithmetic* lives in core/domain/money and is never locale-aware.
 *
 * See contracts/theme-contract.md.
 */

export interface BrandIdentity {
  businessName: string;
  legalName?: string;
  tagline?: string;
  logo: {
    /** Path to the gate mark SVG. Fill is currentColor. */
    mark: string;
    /** Wordmark is rendered as letter-spaced type, not as an asset. */
    wordmarkText: string;
    wordmarkTracking: string;
  };
  /** Clear space around the mark, in multiples of its height. */
  clearSpaceRatio: number;
}

export interface BrandLocale {
  currencyCode: string;
  currencySymbol: string;
  /** Decimal places. PHP = 2. Drives the Money type's minor units. */
  minorUnits: number;
  locale: string;
  timezone: string;
}

export interface BrandReceipt {
  header?: string;
  footer?: string;
  showNutrition: boolean;
  /**
   * Non-accreditation notice. REQUIRED and not removable through
   * configuration (FR-034). A business may edit header and footer; it may not
   * edit this away, because doing so would misrepresent the receipt's status.
   */
  complianceNotice: string;
}

export interface BrandCopy {
  orderTypes: Record<string, string>;
  tenderTypes: Record<string, string>;
}

/**
 * Content for the WiFi voucher ticket (specs/003-wifi-voucher-tickets,
 * contracts/peripheral-wifi-voucher.md). The printing module
 * (`wifi-voucher-layout.ts`) lays this out; it owns no wording of its own,
 * the same separation `ticket-layout.ts` already keeps from `TicketDocument`.
 */
export interface WifiVoucherTicketCopy {
  heading: string;
  instructions: string[];
  tagline: string;
}

export interface BrandConfig {
  identity: BrandIdentity;
  locale: BrandLocale;
  receipt: BrandReceipt;
  copy: BrandCopy;
  wifiVoucher: WifiVoucherTicketCopy;
}

export const brand: BrandConfig = {
  identity: {
    businessName: "HINTO Daily",
    tagline: "Slow down, stay sharp.",
    logo: {
      mark: "/src/theme/assets/mark.svg",
      wordmarkText: "HINTO",
      wordmarkTracking: "0.4em",
    },
    clearSpaceRatio: 0.5,
  },

  locale: {
    currencyCode: "PHP",
    currencySymbol: "₱",
    minorUnits: 2,
    locale: "en-PH",
    timezone: "Asia/Manila",
  },

  receipt: {
    header: "HINTO Daily",
    footer: "Free WiFi · 1 hour · hinto.wifi\nNeed more time? Just ask.\n\nSlow down, stay sharp.",
    showNutrition: false,
    complianceNotice: "This is not a BIR-accredited official receipt.",
  },

  copy: {
    orderTypes: {
      dine_in: "Dine in",
      takeout: "Takeout",
    },
    tenderTypes: {
      cash: "Cash",
      ewallet: "E-wallet",
      card: "Card",
    },
  },

  wifiVoucher: {
    heading: "Free WiFi - 1 hour",
    instructions: [
      '1. Connect to "Hinto Free Wifi"',
      "2. Open any website to reach the login page",
      "3. Enter this code and tap Connect",
    ],
    tagline: "Slow down, stay sharp.",
  },
};

export default brand;
